import React from 'react';
import { AlertTriangle, UserRound, Building2 } from 'lucide-react';
import { LT } from '../../contexts/AppCatalogueContext';
import { COPY, tr } from './copy';
import { compIcon, STAGE_TONES, toneFor } from './model';
import { CellText, Competency } from './shared';

/* Rules & scale · the eight moves as a timeline (threshold, number of
 * reviews, who approves, stage change), then the rules as written, the
 * floor rule, the 1–5 scale and the key competencies. Everything comes
 * from the map's data, so HR's edits show up here. */
export default function RulesView({ map, canon, locale }) {
  const steps = map.promotion?.steps || {};
  const months = map.promotion?.months_per_evaluation || 6;
  const moves = map.levels.slice(0, -1).map(l => ({ from: l.n, to: l.n + 1, step: steps[String(l.n)] })).filter(m => m.step);
  const dec = (v) => String(v).replace('.', locale === 'en' ? '.' : ',');

  return (
    <div className="mt-6 space-y-12">
      <section aria-labelledby="cm-moves">
        <h2 id="cm-moves" className="font-display text-[1.6rem]" style={{ color: LT.greenDeep }}>{tr(COPY.transitions, locale)}</h2>
        <p className="text-[13.5px] mt-1" style={{ color: LT.green50 }}>{tr(COPY.transNote, locale)}</p>
        <div className="mt-4 overflow-x-auto pb-1">
          <ol className="min-w-[760px] grid gap-2 list-none p-0 m-0" style={{ gridTemplateColumns: `repeat(${moves.length}, minmax(0, 1fr))` }}>
            {moves.map(({ from, to, step }) => {
              const tone = toneFor(map, to);
              const direction = /direction/.test(step.approval || '');
              return (
                <li key={from} className="flex flex-col" style={{ border: '1px solid rgba(43,65,56,0.12)', background: LT.beigeLight }}>
                  <div className="px-3 py-2 flex items-center justify-between" style={{ background: tone.fill, color: tone.ink }}>
                    <span className="font-num text-[15px]">{from} → {to}</span>
                    {step.stage_change && <span className="h-2 w-2 rounded-full" style={{ background: LT.yellow }} title={tr(COPY.stageChange, locale)} />}
                  </div>
                  <div className="px-3 py-3 flex flex-col gap-2 text-[12.5px]" style={{ color: LT.greenDeep }}>
                    <div>
                      <div className="font-display text-[1.6rem] leading-none font-num">≥ {dec(step.average)}</div>
                      <div className="text-[11px] mt-0.5" style={{ color: LT.green50 }}>{tr(COPY.average, locale)}</div>
                    </div>
                    <div className="flex gap-1" aria-label={tr(COPY.evalCount, locale, { n: step.evaluations })}>
                      {Array.from({ length: Math.max(1, step.evaluations || 1) }).map((_, i) => (
                        <span key={i} className="h-1.5 flex-1 rounded-full" style={{ background: LT.greenDeep }} />
                      ))}
                    </div>
                    <div style={{ color: '#4d6157' }}>
                      {step.evaluations > 1
                        ? tr(COPY.evalsN, locale, { n: step.evaluations, m: step.evaluations * months })
                        : tr(COPY.evals1, locale)}
                    </div>
                    <div className="flex items-center gap-1.5" style={{ color: '#4d6157' }}>
                      {direction ? <Building2 className="h-3.5 w-3.5 shrink-0" /> : <UserRound className="h-3.5 w-3.5 shrink-0" />}
                      <span>{tr(COPY.approval[step.approval], locale)}</span>
                    </div>
                    {step.stage_change && (
                      <span className="self-start text-[10.5px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full" style={{ background: LT.yellowLight }}>{tr(COPY.stageChange, locale)}</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
        {map.floor_rule && (
          <p className="mt-4 flex gap-2 items-start p-4 text-[13.5px] leading-relaxed" style={{ background: LT.yellowLight, color: LT.greenDeep, borderTop: `1px solid ${LT.yellow}` }}>
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {map.floor_rule}
          </p>
        )}
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="min-w-0">
          <h2 className="font-display text-[1.35rem]" style={{ color: LT.greenDeep }}>{tr(COPY.rules, locale)}</h2>
          <div className="mt-3 space-y-4 text-[13.5px] leading-relaxed" style={{ color: '#3a4f44' }}>
            {map.rules.map((r, i) => (
              <div key={i}>
                <h3 className="text-[11.5px] uppercase tracking-[0.1em] mb-1" style={{ color: LT.green50 }}>{r.label}</h3>
                <CellText text={r.text} compact />
              </div>
            ))}
          </div>
        </div>
        <div className="min-w-0">
          <h2 className="font-display text-[1.35rem]" style={{ color: LT.greenDeep }}>{tr(COPY.scale, locale)}</h2>
          {map.scale_note && <p className="text-[13px] mt-2 leading-relaxed" style={{ color: '#4d6157' }}>{map.scale_note}</p>}
          <div className="mt-3 flex h-2.5 overflow-hidden gap-0.5" aria-hidden>
            {map.scale.map(s => (
              <span key={s.score} className="flex-1" style={{ background: s.score < 3 ? '#E7B9AE' : s.score === 3 ? '#D9DED9' : STAGE_TONES[Math.min(3, s.score - 2)].fill }} />
            ))}
          </div>
          <ol className="mt-3 space-y-2 list-none p-0">
            {map.scale.map(s => (
              <li key={s.score} className="flex gap-3 text-[13.5px] leading-snug" style={{ color: '#3a4f44' }}>
                <span className="h-7 w-7 shrink-0 rounded-full grid place-items-center font-num text-[13px]"
                      style={{ background: s.score < 3 ? '#f3dcd7' : s.score === 3 ? LT.beigeWarm : LT.greenSoft, color: LT.greenDeep }}>{s.score}</span>
                <span>{s.text}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="min-w-0">
          <h2 className="font-display text-[1.35rem]" style={{ color: LT.greenDeep }}>{tr(COPY.comps, locale)}</h2>
          <div className="mt-3 space-y-1">
            {map.competencies.map((c, i) => (
              <Competency key={i} comp={c} Icon={compIcon(canon?.competencies?.[i]?.name)} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
